import React, { useMemo } from 'react';

import moment from 'moment';

import BreakTag from 'components/Tags/BreakTag';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { Room } from 'models/hotelDetailsComplete';
import { getFormattedPrice } from 'utils/currency';

import {
  BestOptionLabel,
  NonRefundableIcon,
  RefundableIcon,
  PerNightValue,
  TextIsRefundable,
  TextIsNotRefundable,
  Value,
  RoomTitle,
} from '../../styles';
import {
  CardContainer,
  Content,
  Description,
  NightsAndNumberOfHost,
  ValuesContainer,
  SeeRoomsButton,
  Separator,
  TextFare,
  Tag,
  ContentRefundableItems,
  PayAtDestination,
  PayAtDestinationText,
  CheckCircleIcon,
} from './styles';

interface Props {
  rooms: Room[];
  checkinDate: Date;
  checkoutDate: Date;
  currencyCode: string;
  alternativeCurrencyCode?: string;
}

const ROOM_INDEX_BEST_OPTION = 0;

const BestRoom = ({ rooms, currencyCode, alternativeCurrencyCode, checkinDate, checkoutDate }: Props) => {
  const { t } = useStoreTranslation();
  const formattedPrice = (value: number | undefined, currencyCode: string) => {
    const { formatted } = getFormattedPrice({ value, currency: currencyCode, t });
    return formatted;
  };

  const room = rooms[ROOM_INDEX_BEST_OPTION];

  const havePayAtDestination = room.roomRate.payAtDestination;

  const amountDays = useMemo(() => {
    const iDate = moment(checkinDate, 'YYYY-MM-DD');
    const eDate = moment(checkoutDate, 'YYYY-MM-DD');
    return eDate.diff(iDate, 'days');
  }, [checkinDate, checkoutDate]);

  const perNightPrice = (room.roomRate.price.total / amountDays).toFixed(2);
  const perNightAlternativePrice = room.roomRate.alternativePrice
    ? (room.roomRate.alternativePrice?.total / amountDays).toFixed(2)
    : 0;

  const seeRooms = () => {
    const roomsDiv = document.querySelector('#hotel-details-rooms');
    roomsDiv?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <CardContainer>
      <Content>
        <RoomTitle>{room.description}</RoomTitle>
        <NightsAndNumberOfHost>
          {`${
            amountDays > 1
              ? t('hotelDetails.nightsWithCount_plural', { count: amountDays })
              : t('hotelDetails.nightsWithCount', { count: amountDays })
          },
          ${
            room.adults > 1
              ? t('hotelDetails.adultsWithCount_plural', { count: room.adults })
              : t('hotelDetails.adultsWithCount', { count: room.adults })
          }`}
        </NightsAndNumberOfHost>
        <ValuesContainer>
          <PerNightValue>
            {t('hotelsCard.nightPrice', { value: formattedPrice(Number(perNightPrice), currencyCode) })}
          </PerNightValue>
          <Value>{formattedPrice(room.roomRate.price.total, currencyCode)}</Value>
          {room.roomRate.alternativePrice && (
            <>
              <PerNightValue>
                {t('hotelsCard.nightPrice', {
                  value: formattedPrice(Number(perNightAlternativePrice), alternativeCurrencyCode),
                })}
              </PerNightValue>
              <Value>{formattedPrice(room.roomRate.alternativePrice.total, alternativeCurrencyCode)}</Value>
            </>
          )}
          {room.roomRate.price.breakRulesMessage?.length ? (
            <Tag>
              <BreakTag reason={room.roomRate.price.breakRulesMessage} />
            </Tag>
          ) : (
            <Tag>
              <BestOptionLabel>{t('hotelDetails.bestOption')}</BestOptionLabel>
            </Tag>
          )}
        </ValuesContainer>
        <SeeRoomsButton type="button" onClick={seeRooms}>
          {t('hotelDetails.seeRooms')}
        </SeeRoomsButton>
      </Content>
      <Separator />
      <Content>
        {room.roomRate.textFare && <TextFare>{room.roomRate.textFare}</TextFare>}
        {room.boardDescription && <Description>{room.boardDescription}</Description>}
        {room.roomRate.refundableType === 'refundable' ? (
          <ContentRefundableItems>
            <RefundableIcon />
            <TextIsRefundable>{t('hotelDetails.roomRefundable')}</TextIsRefundable>
          </ContentRefundableItems>
        ) : (
          <TextIsNotRefundable>
            <NonRefundableIcon />
            {t('hotelDetails.roomNonRefundable')}
          </TextIsNotRefundable>
        )}
        {havePayAtDestination && (
          <PayAtDestination>
            <CheckCircleIcon />
            <PayAtDestinationText>{t('hotelsCard.payAtDestination')}</PayAtDestinationText>
          </PayAtDestination>
        )}
      </Content>
    </CardContainer>
  );
};

export default BestRoom;
